/**
 * EventDetailPage — one event, at `/event/events/:eventId`.
 *
 * Route: `/event/events/:eventId`. Reached from a calendar chip, a table row, and from
 * the create dialog's "Do It Later" exit.
 *
 * Follows the Figma's Event Detail Page (frames 1:9871 and 22:12431 — the second is the
 * just-created state). That design has five cards; four are built here:
 *
 *   Event Progress        built — EventProgress, driven by `lifecycleStage`
 *   Basic Information     built — the Figma's six fields, plus Event Sub Type, SFDC Campaign
 *                         ID, Event Hierarchy, Nomination Required / Time and Target (testing
 *                         feedback: the detail page showed none of what Edit Event sets), and
 *                         Parent Event — the event directly above this one in the campaign
 *                         hierarchy, linked to its page (hierarchySync `parentEventIdOf`; none when
 *                         the node above is an external campaign, which is not a parent event).
 *                         Labels in English, matching the Edit Event form's
 *   Event Introduction    built — the event's `description`, rich text with images
 *                         (EventDescriptionView); shown only when there is one
 *   Attendance List       built — this event's nominations, then its campaign members, from
 *                         ListEventAttendees (members joined with their nominations)
 *   Details by BU         removed at PM request (testing doc #33): it counted nominations
 *                         only, and the dashboard below carries the per-BU picture
 *   Dashboard             built — the Octopus QuickSight lifecycle dashboard (the same one as
 *                         the Event Lifecycle Data tab), narrowed to this event through its
 *                         event parameter. The funnel and goal attainment are Octopus's data,
 *                         not something this app can plot, so it is embedded rather than
 *                         drawn; it sits last, below the cards computed here.
 *
 * Child events: when this event has events directly beneath it in the campaign hierarchy
 * (useEventHierarchy `childEvents` — event-under-event only, the rule Parent Event follows), a tab
 * bar sits between Event Progress and Basic Information: "Overall" is this event, then one tab per
 * child, each showing that child's own cards (EventDetailBody, via ChildEventTab). The progress rail
 * and the header's actions stay this event's. The open tab is `?tab=<child event id>`. The body
 * below Event Progress lives in EventDetailBody so each tab renders the same view.
 *
 * The Attendance List reads every page of ListEventAttendees. ListNominations is not used here:
 * it backs the Nomination List page only.
 *
 * Fetches by id through GetEvent rather than reading a cached list: the URL is
 * shareable, so the page has to work on a cold load, and it should show the event as it
 * is now.
 *
 * One action sits beside the page title: `Edit Event`. It is what makes the create flow
 * finishable — "Do It Later" lands the user here with an event that has only a name, and
 * this is the way on to fill in the rest.
 *
 * Gated on role, as the service is: UpdateEvent / PublishEvent admit any marketer or admin on
 * any event (`canManageEvents`), not only the creator — changed at PM request so a marketer can
 * finish and publish a colleague's draft. Hidden from everyone else, for whom both are a 403.
 *
 * `Publish` sits beside it, and only while the event is a DRAFT. This is where the draft
 * ends — editing and publishing are separate decisions, so saving the form does not go
 * live. A marketer can fill in details over several sittings and publish when ready.
 *
 * `Create Child Event` sits beside them, on the same role gate. It creates a DRAFT copy of this
 * event's settings and places it under this event in the campaign hierarchy (utils/childEvent says
 * what is and is not copied, and why), then opens the copy's Edit Event so the marketer can rename it
 * and give it its own SFDC Campaign ID. A confirmation comes first because it writes two things
 * (an event and a hierarchy node) that a stray click should not. If the event is created but the
 * placement fails, the page stays here and says so, with a link to the new event.
 *
 * The status also shows as a badge next to the title, so "why is there a Publish button"
 * and "what state is this in" have the same answer in the same glance.
 *
 * `Nominate` is the BD's action here (Event Hub Testing doc #50), on the same condition as the
 * home page's Nominate Now (utils/nominatableEvents `isNominatable`) and for the same roles the
 * service lets nominate (`canNominate`), so the two entry points cannot disagree about whether
 * an event is open — including the BU rule: only a BD whose BU is one of the event's sees it. It
 * goes to the nomination form with this event picked.
 *
 * A BD who is not also a marketer or admin (`seesOnlyOwnBuEvents`) sees only events for their own
 * BUs, as on the calendar: an event outside them — reached by a shared or typed link — shows a
 * "not available" notice instead of its content, as does an event with no BUs. The BUs come from
 * GET /me; while that loads the page waits, and if it fails nothing is shown (fail-closed). UX
 * only: GetEvent still returns the event, so the real guard is the service's.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ContentLayout,
  Header,
  Link,
  Modal,
  SpaceBetween,
  Tabs,
} from '@amzn/awsui-components-react';
import { canManageEvents, canNominate, seesOnlyOwnBuEvents } from '../../utils/authorization';
import { eventIsForBusinessUnits, isNominatable } from '../../utils/nominatableEvents';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import { EventProgress } from '../../components/EventProgress';
import { useNominationBusinessUnits } from '../../hooks/useNominationBusinessUnits';
import { eventHubCalendarClient, type Event } from '../../client/EventCalendarClient';
import { useEventHierarchy } from '../../hooks/useEventHierarchy';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import { CHILD_NAME_SUFFIX, createChildEvent } from '../../utils/childEvent';
import EventDetailBody from './EventDetailBody';
import ChildEventTab from './ChildEventTab';

/** The Overall tab's id; a child's tab is its event id. */
const OVERALL_TAB = 'overall';


export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const roles = useEventHubRoles();
  const canManage = canManageEvents(roles);
  const { login } = useEventHubLogin();
  // The BUs an event must be for, for this caller to nominate to it (utils/nominatableEvents).
  const { businessUnits: bdBusinessUnits, loading: loadingBus } = useNominationBusinessUnits(
    canNominate(roles),
  );
  /* A BD-only reader waits for their BUs before anything of the event shows (see the header). */
  const buScoped = seesOnlyOwnBuEvents(roles);
  const waitingForBus = buScoped && loadingBus;

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  /**
   * Why a publish attempt failed. Kept apart from the load error: the event is still on
   * screen and still readable, so this is a notice about one action rather than the page
   * having failed.
   */
  const [publishError, setPublishError] = useState<string | null>(null);
  /** The Create Child Event confirmation: open, and whether it is creating. */
  const [confirmingChild, setConfirmingChild] = useState(false);
  const [creatingChild, setCreatingChild] = useState(false);
  /** Why the child event could not be created; shown in the still-open confirmation. */
  const [childError, setChildError] = useState<string | null>(null);
  /** A child that was created but could not be placed under this event. */
  const [unplacedChild, setUnplacedChild] = useState<{ event: Event; error: string } | null>(null);

  /** This event's hierarchy: the tree and Parent Event on Overall, and the child events for the tabs. */
  const hierarchy = useEventHierarchy(eventId);
  /**
   * The open tab, in the URL (`?tab=<child event id>`) so a child's tab can be shared and survives a
   * reload. Overall is the absence of the parameter; an id that is not (or no longer) a child of this
   * event falls back to it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && hierarchy.childEvents.some((c) => c.eventId === requestedTab)
      ? requestedTab
      : OVERALL_TAB;

  useEventHubLoading(loading || waitingForBus);

  useEffect(() => {
    if (!eventId) {

      // Only reachable by hand-editing the URL, but it must not fetch `undefined`.
      setError('No event id in the URL.');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(null);

    eventHubCalendarClient()
      .getEvent(eventId, { signal: controller.signal })
      .then((result) => {
        if (cancelled) return;
        setEvent(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setEvent(null);
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [eventId]);

  /**
   * Publishes, then shows the event the service handed back rather than re-reading it —
   * PublishEvent returns the updated resource, so a second round trip could only disagree
   * with what just happened.
   *
   * A rejection here is ordinary, not exceptional: the service refuses to publish an event
   * without dates, or a nomination-enabled one without its window, and that is exactly the
   * state an event created from a name alone is in. The message names the field to go and
   * fill in, so it is shown as-is.
   */
  async function onPublish() {
    if (!eventId) return;
    setPublishing(true);
    setPublishError(null);
    try {
      setEvent(await eventHubCalendarClient().publishEvent(eventId));
    } catch (e: unknown) {
      setPublishError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  }

  async function onCreateChild() {
    if (!event) return;
    setCreatingChild(true);
    setChildError(null);
    try {
      // The parent's node as the hierarchy read found it, so it is not looked up a second time.
      const result = await createChildEvent(
        eventHubCalendarClient(),
        event,
        login,
        hierarchy.anchorNodeId,
      );
      setConfirmingChild(false);
      if (result.placementError) {
        setUnplacedChild({ event: result.event, error: result.placementError });
      } else {
        navigate(`/event/events/${result.event.eventId}/edit`);
      }
    } catch (e: unknown) {
      setChildError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreatingChild(false);
    }
  }

  /* An event outside the BD's BUs, or with none: shown as unavailable. `[]` (lookup failed)
     matches nothing, so a failure hides the event rather than revealing it. */
  const outsideBus =
    buScoped && !!event && !waitingForBus && !eventIsForBusinessUnits(event, bdBusinessUnits ?? []);
  /* The event as this reader may see it: withheld while their BUs load and when outside them. */
  const visibleEvent = waitingForBus || outsideBus ? null : event;

  const isDraft = visibleEvent?.contentStatus === 'DRAFT';
  const showNominate =
    !!event && canNominate(roles) && !loadingBus && isNominatable(event, new Date(), bdBusinessUnits);

  return (
    <ContentLayout
      header={
        <>
          <BreadcrumbGroup
            // Two levels, as the Figma draws them. The last crumb is the literal "Event
            // Detail Page" rather than the event's name, which is already the h1.
            items={[
              { text: 'Event Hub', href: '/event' },
              { text: 'Event Detail Page', href: '' },
            ]}
            onFollow={(e) => {
              e.preventDefault();
              if (e.detail.href) navigate(e.detail.href);
            }}
          />
          {/* The event's name is the page title. While it is still loading the heading
              is left empty rather than filled with "Loading…", so the title does not
              change twice. */}
          <Header
            variant="h1"
            /* Beside the title, as the Figma places it. Withheld until the event has
               loaded: an edit link for an event not yet on screen has nothing to edit. */
            actions={
              (canManage || showNominate) && visibleEvent ? (
                <SpaceBetween direction="horizontal" size="xs">
                  {canManage ? (
                    <Button onClick={() => navigate(`/event/events/${eventId}/edit`)}>
                      Edit Event
                    </Button>
                  ) : null}
                  {canManage ? (
                    <Button
                      onClick={() => {
                        setChildError(null);
                        setConfirmingChild(true);
                      }}
                    >
                      Create Child Event
                    </Button>
                  ) : null}
                  {/* Only while it is a draft. Publishing something already published is not
                      something the service does, and the design has no Unpublish. */}
                  {canManage && isDraft ? (
                    <Button variant="primary" loading={publishing} onClick={onPublish}>
                      Publish
                    </Button>
                  ) : null}
                  {showNominate ? (
                    <Button
                      variant="primary"
                      onClick={() => navigate(`/event/nominations/new?eventId=${event.eventId}`)}
                    >
                      Nominate
                    </Button>
                  ) : null}
                </SpaceBetween>
              ) : undefined
            }
          >
            {/* The badge rides with the title, as the Figma draws it. The status is ALSO a
                Basic Information field below, which the design shows too — the badge answers
                "why is there a Publish button" at the point the button is, and the field
                answers "what are this event's attributes". */}
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <span>{visibleEvent?.eventName ?? ''}</span>
              {isDraft ? <Badge color="blue">Draft</Badge> : null}
            </SpaceBetween>
          </Header>
        </>
      }
    >
      {publishError ? (
        <Box padding={{ bottom: 'l' }}>
          <Alert
            type="error"
            dismissible
            onDismiss={() => setPublishError(null)}
            header="Could not publish this event"
          >
            {publishError}
          </Alert>
        </Box>
      ) : null}

      {unplacedChild ? (
        <Box padding={{ bottom: 'l' }}>
          <Alert
            type="warning"
            dismissible
            onDismiss={() => setUnplacedChild(null)}
            header="Child event created, but not placed under this event"
          >
            <Link
              href={`/event/events/${unplacedChild.event.eventId}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/event/events/${unplacedChild.event.eventId}`);
              }}
            >
              {unplacedChild.event.eventName}
            </Link>{' '}
            exists as a draft, but adding it to this event's hierarchy failed: {unplacedChild.error}.
            Add it under Event Hierarchy on this event's Edit Event.
          </Alert>
        </Box>
      ) : null}

      {visibleEvent && confirmingChild ? (
        <Modal
          visible
          onDismiss={() => !creatingChild && setConfirmingChild(false)}
          header="Create Child Event"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  disabled={creatingChild}
                  onClick={() => setConfirmingChild(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" loading={creatingChild} onClick={onCreateChild}>
                  Create
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            {childError ? (
              <Alert type="error" header="Failed to create the child event">
                {childError}
              </Alert>
            ) : null}
            <Box>
              Creates a draft named{' '}
              <Box variant="strong" display="inline">
                {visibleEvent.eventName}
                {CHILD_NAME_SUFFIX}
              </Box>{' '}
              under this event in its hierarchy, with this event&apos;s settings.
            </Box>
            <Box color="text-body-secondary">
              Not copied: the SFDC Campaign ID (each event needs its own) and the Event
              Introduction. You can fill both in on the Edit Event page, which opens next.
            </Box>
          </SpaceBetween>
        </Modal>
      ) : null}

      {error ? (
        <Alert type="error" header="Failed to load this event">
          {error}
        </Alert>
      ) : loading || waitingForBus ? null /* the layout's overlay shows meanwhile */ : outsideBus ? (
        <Alert type="info" header="This event is not available">
          It is not for any of your business units.
        </Alert>
      ) : event ? (
        <>
          <EventProgress lifecycleStage={event.lifecycleStage} />
          {/* Tabs only when there are child events: Overall is this event, then one per child. Lazy, so
              a tab is rendered when first opened and then kept — switching back does not reload its
              attendees or re-embed its dashboard. */}
          {hierarchy.childEvents.length ? (
            <Box padding={{ top: 'l' }}>
              <Tabs
                activeTabId={activeTab}
                onChange={({ detail }) =>
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      if (detail.activeTabId === OVERALL_TAB) next.delete('tab');
                      else next.set('tab', detail.activeTabId);
                      return next;
                    },
                    { replace: true },
                  )
                }
                tabs={[
                  {
                    id: OVERALL_TAB,
                    label: 'Overall',
                    contentRenderStrategy: 'lazy',
                    content: (
                      <EventDetailBody event={event} roles={roles} login={login} hierarchy={hierarchy} />
                    ),
                  },
                  ...hierarchy.childEvents.map((child) => ({
                    id: child.eventId!,
                    label: child.eventName || child.eventId!,
                    contentRenderStrategy: 'lazy' as const,
                    content: (
                      <ChildEventTab
                        eventId={child.eventId!}
                        parent={{ eventId: event.eventId!, eventName: event.eventName }}
                        roles={roles}
                        login={login}
                      />
                    ),
                  })),
                ]}
              />
            </Box>
          ) : (
            <EventDetailBody event={event} roles={roles} login={login} hierarchy={hierarchy} />
          )}

        </>
      ) : null}
    </ContentLayout>
  );
}
